/**
 * Doomsworn — the write path
 *
 * Reading engine state was the easy half. This is what actually spends a
 * character: a Draw that charges Debt, mints a Lien, fires a Calling and moves
 * the Tilt, then persists all of it so the next scene inherits it.
 *
 * Character state rides in `Character.systemState` and saves through the normal
 * character sync — no bespoke persistence. Only the campaign-scoped Tilt has
 * its own table, and only a DM may write it (solo players hold the DM role, so
 * solo play works).
 */

import { supabase } from './supabase';
import {
  draw as engineDraw,
  loadCharacterState,
  writeCharacterState,
  readCampaignState,
  spendDoomDie,
  growDoomDie,
  clampTilt,
  type Power,
  type PactTier,
  type DrawResult,
  type Tilt,
} from '@machina/system-doomsworn';
import type { Character } from '@machina/core';
import { useCharacterStore } from '../stores/characterStore';

const TABLE = 'machina_doomsworn_campaign_state';

// ---------------------------------------------------------------------------
// THE TILT
// ---------------------------------------------------------------------------

export async function loadTilt(campaignId: string): Promise<Tilt> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('tilt, schema_version')
    .eq('campaign_id', campaignId)
    .maybeSingle();

  if (error) {
    console.warn('[doomsworn] Could not read the Tilt, assuming the Balance holds:', error.message);
    return 0;
  }

  return readCampaignState(data ? { tilt: data.tilt, schemaVersion: data.schema_version } : null).tilt;
}

/**
 * Write the Tilt.
 *
 * Clamped before it goes near the DB — the column has a ±10 check constraint,
 * and a rejected write here would mean a Draw that charged the character but
 * left the needle behind, which is the one inconsistency this pillar cannot
 * afford.
 */
export async function saveTilt(campaignId: string, tilt: Tilt): Promise<void> {
  const { error } = await supabase
    .from(TABLE)
    .upsert(
      {
        campaign_id: campaignId,
        tilt: clampTilt(tilt),
        schema_version: 1,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'campaign_id' },
    );

  if (error) console.warn('[doomsworn] Could not persist the Tilt:', error.message);
}

// ---------------------------------------------------------------------------
// THE DRAW
// ---------------------------------------------------------------------------

export interface PerformDrawInput {
  character: Character;
  campaignId: string;
  power: Power;
  verb: string;
  object: string;
  tier: PactTier;
  /** ATTUNEMENT modifier. Absent Attunement is 0, never -5. */
  attunementMod: number;
  lienClaim?: string;
  callingAsk?: string;
}

/**
 * Draw on a Power and persist everything it cost.
 *
 * The effect ALWAYS happens — this returns what it cost, never whether it
 * worked. Throws only on an invalid Draw (a Hungerbound asked past its Tier
 * cap), which is a caller bug rather than a play outcome.
 */
export async function performDraw(input: PerformDrawInput): Promise<DrawResult> {
  const { character, campaignId } = input;

  const state = loadCharacterState({ id: character.id, systemState: character.systemState });
  const tilt = await loadTilt(campaignId);

  const result = engineDraw(state.ledger, {
    power: input.power,
    verb: input.verb,
    object: input.object,
    tier: input.tier,
    attunementMod: input.attunementMod,
    tilt,
    at: Date.now(),
    lienClaim: input.lienClaim,
    callingAsk: input.callingAsk,
  });

  const next = {
    ...state,
    ledger: result.ledger,
    wear: [...state.wear, ...result.wear],
  };

  const updated = writeCharacterState({ id: character.id, systemState: character.systemState }, next);
  // updateCharacter syncs to Supabase on its own; persisting the sheet is not
  // this module's job, and doing it here would double-write.
  useCharacterStore.getState().updateCharacter(character.id, {
    systemState: updated.systemState,
  } as Partial<Character>);

  await saveTilt(campaignId, result.tilt);

  return result;
}

// ---------------------------------------------------------------------------
// THE DOOM
// ---------------------------------------------------------------------------

/**
 * Write a character's declared Doom.
 *
 * Their words, stored verbatim. Nothing tidies the phrasing — the GM is under
 * standing instruction never to rewrite it, and it would be a poor look for the
 * engine to do what the narrator is forbidden to.
 */
export function declareDoom(character: Character, declared: string): void {
  const state = loadCharacterState({ id: character.id, systemState: character.systemState });
  const updated = writeCharacterState(
    { id: character.id, systemState: character.systemState },
    { ...state, doom: { ...state.doom, declared } },
  );
  useCharacterStore.getState().updateCharacter(character.id, {
    systemState: updated.systemState,
  } as Partial<Character>);
}

export interface DoomSpendOutcome {
  bonus: number;
  clock: number;
  arrived: boolean;
  wearGained: number;
  /** True when this spend also stepped the die up the track. */
  grew: boolean;
}

/**
 * Spend the Doom Die on a roll the player has already seen, and persist it.
 *
 * Advancement is by concession, and spending the die IS a concession — so this
 * is also where the die grows. More power, and a faster arrival: the trade is
 * the whole system.
 */
export function spendDoom(character: Character, growthSteps: readonly number[]): DoomSpendOutcome {
  const state = loadCharacterState({ id: character.id, systemState: character.systemState });
  const spend = spendDoomDie(state.doom);

  const concessions = state.concessions + 1;
  const grew = growthSteps.includes(concessions);
  const doom = grew ? { ...spend.state, die: growDoomDie(spend.state.die) } : spend.state;

  const updated = writeCharacterState(
    { id: character.id, systemState: character.systemState },
    {
      ...state,
      doom,
      concessions,
      concessionLog: [...state.concessionLog, `Spent the Doom Die (clock ${spend.state.clock})`],
      wear: spend.wearGained
        ? [
            ...state.wear,
            {
              id: `WEAR-doom-${character.id}-${spend.state.clock}`,
              source: 'doom-clock' as const,
              description: `Doom Clock at ${spend.state.clock}: ${state.doom.declared || 'the end they wrote down'}`,
              takenAt: Date.now(),
            },
          ]
        : state.wear,
    },
  );

  useCharacterStore.getState().updateCharacter(character.id, {
    systemState: updated.systemState,
  } as Partial<Character>);

  return {
    bonus: spend.bonus,
    clock: spend.state.clock,
    arrived: spend.arrived,
    wearGained: spend.wearGained,
    grew,
  };
}
